/**
 * FE-C2g-10: Shared legacy group route probe — apiBase-aware, no double /api.
 */

import { apiBase } from "./api.js";
import {
  buildDeleteGroupCardsPath,
  buildDeleteNotificationGroupsPath,
  buildPostGroupCardsDeletePath,
  buildPostNotificationGroupsDeletePath,
} from "./group-console-api.js";

/** True when apiBase() already ends with /api (Traefik StripPrefix). */
export function apiBaseUsesApiPrefix() {
  const base = String(apiBase() || "").replace(/\/$/, "");
  if (!base) return false;
  try {
    const u = base.startsWith("http") ? new URL(base) : new URL(base, location.origin);
    const p = u.pathname.replace(/\/$/, "");
    return p === "/api" || p.endsWith("/api");
  } catch {
    return base.endsWith("/api");
  }
}

/**
 * Ordered contract-relative paths to try (never yields /api/api/… on Traefik).
 * @param {string} contractPath
 * @param {string} [cachedPrefix] — "" or "/api" from localStorage caps
 */
export function contractPathProbeOrder(contractPath, cachedPrefix = "") {
  const cp = String(contractPath || "");
  if (!cp.startsWith("/")) return [cp];
  if (apiBaseUsesApiPrefix()) return [cp];
  const mirror = `/api${cp}`;
  if (cachedPrefix === "/api") return [mirror, cp];
  return [cp, mirror];
}

/** @param {unknown} err */
export function isContractRouteMissingError(err) {
  const msg = String((err && err.message) || err || "");
  return msg.includes("404") || msg.includes("405") || msg.includes("501");
}

/**
 * @param {(path: string, opts?: object) => Promise<unknown>} apiFn
 * @param {string} contractPath
 * @param {object} [opts]
 * @param {{ prefix?: string, onPrefixResolved?: (prefix: string) => void }} [caps]
 */
export async function tryContractApiWithProbe(apiFn, contractPath, opts, caps = {}) {
  const lastErrs = [];
  const paths = contractPathProbeOrder(contractPath, caps.prefix || "");
  for (const p of paths) {
    try {
      const r = await apiFn(p, opts);
      const px = p.startsWith("/api/") ? "/api" : "";
      if (caps.onPrefixResolved && caps.prefix !== px) caps.onPrefixResolved(px);
      return r;
    } catch (e) {
      lastErrs.push(e);
      if (!isContractRouteMissingError(e)) throw e;
    }
  }
  throw lastErrs[lastErrs.length - 1] || new Error("group route unavailable");
}

/**
 * @param {(path: string, opts?: object) => Promise<unknown>} tryContractFn
 * @param {"post"|"delete"} mode
 * @param {string} groupKey
 * @param {string} [ownerAdmin]
 */
export async function tryGroupDeleteWithProbe(tryContractFn, mode, groupKey, ownerAdmin) {
  const lastErrs = [];
  const builders = mode === "post"
    ? [buildPostNotificationGroupsDeletePath, buildPostGroupCardsDeletePath]
    : [buildDeleteNotificationGroupsPath, buildDeleteGroupCardsPath];
  const method = mode === "post" ? "POST" : "DELETE";
  for (const build of builders) {
    try {
      return await tryContractFn(build(groupKey, ownerAdmin), { method });
    } catch (e) {
      lastErrs.push(e);
    }
  }
  throw lastErrs[lastErrs.length - 1] || new Error("group delete route unavailable");
}
